'use client';

import { useEffect } from 'react';

import { pushSignupEvent } from '../gtm/data-layer';
import { useAnalytics } from '../analytics/analytics-context';

import { claimSignupReport, clearSignupSignal, readSignupSignal } from './signup-signal';
import {
	buildTouchAttributionPayload,
	buildTouchPersonProperties,
} from './touch-person-properties';
import { recordLanding } from './touch-storage';

/**
 * Fires everything a completed signup owes to the ad platforms, exactly once.
 *
 * Signup completes server-side and the browser lands via a 302, so this runs on
 * the first client render after that landing, driven by the `naumu-signup`
 * cookie the backend set on its way out. Three things happen together:
 *
 *   1. `dataLayer.push({ event: 'signup', user_id, method })` — the GTM Custom
 *      Event trigger that fires the Google Ads conversion tag.
 *   2. `POST /api/user/touch-attribution` — writes first/last touch onto the
 *      User node, so campaign → account is joinable in the database, not just
 *      in an ad dashboard.
 *   3. PostHog person properties — the same values on the person, so PostHog
 *      funnels can be cut by campaign.
 *
 * Guarded twice: the cookie is consumed (cleared) immediately, and a
 * sessionStorage key keyed by user id makes a reload or a StrictMode double
 * effect a no-op.
 */
export const SignupAttributionReporter = () => {
	const analytics = useAnalytics();

	useEffect(() => {
		const signal = readSignupSignal();
		if (!signal) return;

		// Consume first: even if anything below throws, we never re-fire a
		// conversion on the next render.
		clearSignupSignal();
		if (!claimSignupReport(signal.userId)) return;

		pushSignupEvent({ userId: signal.userId, method: signal.method });

		// Guarantees a record exists even if this effect runs before the
		// provider's. `includeReferrer: false` because the referrer on a signup
		// landing is the identity provider we just bounced through, never a
		// marketing source (buildTouchSnapshot filters those hosts too).
		const record = recordLanding({ includeReferrer: false });
		if (!record) return;

		analytics.setPersonProperties(buildTouchPersonProperties(record));

		void fetch('/api/user/touch-attribution', {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(buildTouchAttributionPayload(record)),
			keepalive: true,
		}).catch(() => {
			// Attribution is best-effort; a failed write must never surface to a
			// user who has just signed up.
		});
	}, [analytics]);

	return null;
};
